import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface ExitLessonModalProps {
  onKeepGoing: () => void;
  onExit?: () => void;
  onSkipToExitTicket?: () => void;
  isSkippingToExitTicket?: boolean;
}

const ExitLessonModal: React.FC<ExitLessonModalProps> = ({
  onKeepGoing,
  onExit,
  onSkipToExitTicket,
  isSkippingToExitTicket = false,
}) => {
  const exitTicketRequired = !onExit && !!onSkipToExitTicket;
  const exitRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => exitRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onKeepGoing();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
        ),
      ).filter((el) => !el.hasAttribute("disabled"));

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onKeepGoing]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onKeepGoing()}
    >
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-modal-title"
        aria-describedby="exit-modal-desc"
        className="relative bg-white rounded-3xl shadow-2xl w-[90%] max-w-[400px] px-8 pt-11 pb-7 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div
          className="w-[74px] h-[74px] rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: "#FFF4DB" }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
              stroke="#f5a623"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="16 17 21 12 16 7"
              stroke="#f5a623"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="21"
              y1="12"
              x2="9"
              y2="12"
              stroke="#f5a623"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <h2
          id="exit-modal-title"
          className="text-[22px] font-bold text-[#1a1a1a] mb-2.5"
          style={{ fontFamily: "Nunito, sans-serif" }}
        >
          Exit lesson
        </h2>

        <p
          id="exit-modal-desc"
          className="text-[15px] text-[#6b6b6b] mb-7 leading-relaxed whitespace-pre-line"
          style={{ fontFamily: "Mulish, sans-serif" }}
        >
          {exitTicketRequired
            ? "You need to complete the Exit Ticket before you can leave this lesson."
            : onSkipToExitTicket
              ? "You haven\u2019t completed the Exit Ticket yet.\nExit anyway?"
              : "Your progress will be saved."}
        </p>

        {onSkipToExitTicket && exitTicketRequired && (
          <button
            ref={exitRef}
            onClick={isSkippingToExitTicket ? undefined : onSkipToExitTicket}
            disabled={isSkippingToExitTicket}
            className="mb-5 w-full py-3.5 px-7 text-[16px] font-bold text-white rounded-[10px] transition-opacity duration-150 can-hover:hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "#f5a623", fontFamily: "Nunito, sans-serif" }}
          >
            {isSkippingToExitTicket ? "Loading..." : "Take Exit Ticket"}
          </button>
        )}

        {onSkipToExitTicket && !exitTicketRequired && (
          <button
            onClick={isSkippingToExitTicket ? undefined : onSkipToExitTicket}
            disabled={isSkippingToExitTicket}
            className="mb-5 text-[15px] font-semibold underline transition-opacity duration-150 can-hover:hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: "#1a1a1a", fontFamily: "Mulish, sans-serif" }}
          >
            {isSkippingToExitTicket ? "Loading..." : "Skip to Exit Ticket"}
          </button>
        )}

        <div className="flex items-center justify-center gap-4">
          {onExit && (
            <button
              ref={exitRef}
              onClick={onExit}
              className="py-3.5 px-7 text-[16px] font-bold text-white rounded-[10px] transition-opacity duration-150 can-hover:hover:opacity-90 active:opacity-80"
              style={{
                background: "#ea615d",
                fontFamily: "Nunito, sans-serif",
              }}
              aria-label="Exit lesson"
            >
              Exit lesson
            </button>
          )}

          <button
            onClick={onKeepGoing}
            className="py-3.5 px-3 text-[16px] font-bold rounded-[10px] transition-opacity duration-150 can-hover:hover:opacity-70 active:opacity-60"
            style={{
              color: "#6b6b6b",
              background: "transparent",
              fontFamily: "Nunito, sans-serif",
            }}
          >
            Keep going
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExitLessonModal;
